// client/src/admin/support/AdminSupportTickets.js
import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  TextField,
  Button,
  Avatar,
  useTheme,
  alpha,
  IconButton,
  Tooltip,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  SupportAgent as SupportIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  ArrowBack as BackIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import PageHeader from "../../common/PageHeader";
import CommonDialog from "../../common/CommonDialog";

const lang_defaults = {
  supportTickets: "Support Tickets",
  supportTicketsSubtitle: "View and reply to user support requests",
  subject: "Subject",
  status: "Status",
  user: "User",
  createdAt: "Created",
  actions: "Actions",
  viewReplies: "View & Reply",
  noTickets: "No tickets found",
  open: "Open",
  answered: "Answered",
  closed: "Closed",
  typeReply: "Type your reply...",
  send: "Send",
  updateStatus: "Update Status",
  ticketDetails: "Ticket Details",
  originalMessage: "Original Message",
  replies: "Replies",
  cancel: "Cancel",
};

const STATUS_COLORS = {
  open: "error",
  answered: "success",
  closed: "default",
};

const AdminSupportTickets = ({ lang: propLang }) => {
  const theme = useTheme();
  const { hitAxios } = useContext(GlobalContext);
  const { lang: ctxLang } = useContext(TranslateContext);
  const t = (k) => propLang?.[k] || ctxLang?.[k] || lang_defaults[k] || k;

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [replies]);

  async function fetchTickets() {
    setLoading(true);
    const res = await hitAxios({
      path: "/api/support/admin/list",
      admin: true,
      post: false,
      showLoading: false,
    });
    setLoading(false);
    if (res?.data?.success) setTickets(res.data.data || []);
  }

  async function openTicket(ticket) {
    setSelectedTicket(ticket);
    setDetailOpen(true);
    const res = await hitAxios({
      path: `/api/support/admin/replies/${ticket.ticket_id}`,
      admin: true,
      post: false,
      showLoading: false,
    });
    if (res?.data?.success) {
      setReplies(res.data.data || []);
      setSelectedTicket(res.data.ticket);
    }
  }

  async function handleSendReply() {
    if (!replyText.trim() || !selectedTicket) return;
    setSending(true);
    const res = await hitAxios({
      path: "/api/support/admin/reply",
      admin: true,
      post: true,
      obj: { ticket_id: selectedTicket.ticket_id, message: replyText.trim() },
    });
    setSending(false);
    if (res?.data?.success) {
      setReplyText("");
      // Refresh replies
      const r2 = await hitAxios({
        path: `/api/support/admin/replies/${selectedTicket.ticket_id}`,
        admin: true,
        post: false,
        showLoading: false,
        showSnackbar: false,
      });
      if (r2?.data?.success) {
        setReplies(r2.data.data || []);
        setSelectedTicket(r2.data.ticket);
      }
      fetchTickets();
    }
  }

  async function handleStatusChange(ticket_id, status) {
    const res = await hitAxios({
      path: "/api/support/admin/update-status",
      admin: true,
      post: true,
      obj: { ticket_id, status },
    });
    if (res?.data?.success) {
      fetchTickets();
      if (selectedTicket?.ticket_id === ticket_id) {
        setSelectedTicket((p) => ({ ...p, status }));
      }
    }
  }

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "—";

  return (
    <Box>
      <PageHeader
        title={t("supportTickets")}
        subtitle={t("supportTicketsSubtitle")}
        icon={SupportIcon}
        primaryAction={
          <Tooltip title={t("refresh") || "Refresh"}>
            <IconButton
              size="small"
              onClick={fetchTickets}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
              }}
            >
              <RefreshIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        }
      />

      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  t("subject"),
                  t("user"),
                  t("status"),
                  t("createdAt"),
                  t("actions"),
                ].map((h) => (
                  <TableCell
                    key={h}
                    sx={{ fontWeight: 700, fontSize: "0.75rem", py: 1.5 }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    align="center"
                    sx={{ py: 5, color: "text.disabled" }}
                  >
                    {loading ? "Loading..." : t("noTickets")}
                  </TableCell>
                </TableRow>
              ) : (
                tickets.map((ticket) => (
                  <TableRow key={ticket.ticket_id} hover>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        noWrap
                        sx={{ maxWidth: 220 }}
                      >
                        {ticket.subject}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            fontSize: 10,
                            bgcolor: "primary.main",
                          }}
                        >
                          {ticket.user_name?.[0]?.toUpperCase() || "U"}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            display="block"
                            noWrap
                            sx={{ maxWidth: 130 }}
                          >
                            {ticket.user_name || "—"}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            noWrap
                            sx={{ maxWidth: 130, display: "block" }}
                          >
                            {ticket.user_email || "—"}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(ticket.status) || ticket.status}
                        size="small"
                        color={STATUS_COLORS[ticket.status] || "default"}
                        sx={{ fontSize: "0.65rem", height: 20 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(ticket.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openTicket(ticket)}
                          sx={{
                            borderRadius: 1.5,
                            textTransform: "none",
                            fontSize: "0.72rem",
                            py: 0.4,
                          }}
                        >
                          {t("viewReplies")}
                        </Button>
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <Select
                            value={ticket.status}
                            onChange={(e) =>
                              handleStatusChange(
                                ticket.ticket_id,
                                e.target.value,
                              )
                            }
                            sx={{ fontSize: "0.72rem", borderRadius: 1.5 }}
                          >
                            <MenuItem value="open" sx={{ fontSize: "0.75rem" }}>
                              {t("open")}
                            </MenuItem>
                            <MenuItem
                              value="answered"
                              sx={{ fontSize: "0.75rem" }}
                            >
                              {t("answered")}
                            </MenuItem>
                            <MenuItem
                              value="closed"
                              sx={{ fontSize: "0.75rem" }}
                            >
                              {t("closed")}
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ── Ticket Detail Dialog ── */}
      <CommonDialog
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setReplyText("");
          setReplies([]);
        }}
        title={selectedTicket?.subject || t("ticketDetails")}
        subtitle={
          selectedTicket
            ? `${selectedTicket.user_name || ""} · ${selectedTicket.user_email || ""}`
            : ""
        }
        icon={SupportIcon}
        maxWidth="sm"
      >
        {selectedTicket && (
          <Stack spacing={2}>
            {/* Status chip */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Chip
                label={t(selectedTicket.status) || selectedTicket.status}
                size="small"
                color={STATUS_COLORS[selectedTicket.status] || "default"}
                sx={{ fontSize: "0.7rem" }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={selectedTicket.status}
                  onChange={(e) =>
                    handleStatusChange(selectedTicket.ticket_id, e.target.value)
                  }
                  sx={{ fontSize: "0.75rem", borderRadius: 1.5 }}
                >
                  <MenuItem value="open" sx={{ fontSize: "0.75rem" }}>
                    {t("open")}
                  </MenuItem>
                  <MenuItem value="answered" sx={{ fontSize: "0.75rem" }}>
                    {t("answered")}
                  </MenuItem>
                  <MenuItem value="closed" sx={{ fontSize: "0.75rem" }}>
                    {t("closed")}
                  </MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <Divider />

            {/* Original message */}
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              }}
            >
              <Typography
                variant="caption"
                color="text.disabled"
                fontWeight={700}
                display="block"
                mb={0.5}
              >
                {t("originalMessage")}
              </Typography>
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}
              >
                {selectedTicket.message}
              </Typography>
            </Box>

            {/* Replies thread */}
            {replies.length > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  fontWeight={700}
                  display="block"
                  mb={1}
                >
                  {t("replies")}
                </Typography>
                <Stack
                  spacing={1.5}
                  sx={{ maxHeight: 280, overflowY: "auto", pr: 0.5 }}
                >
                  {replies.map((reply) => {
                    const isAdmin = reply.sender_role === "admin";
                    return (
                      <Box
                        key={reply.id}
                        sx={{
                          display: "flex",
                          flexDirection: isAdmin ? "row-reverse" : "row",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            flexShrink: 0,
                            bgcolor: isAdmin
                              ? "primary.main"
                              : "action.selected",
                          }}
                        >
                          {isAdmin ? (
                            <AdminIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <PersonIcon sx={{ fontSize: 14 }} />
                          )}
                        </Avatar>
                        <Box
                          sx={{
                            maxWidth: "80%",
                            p: 1.25,
                            borderRadius: isAdmin
                              ? "12px 4px 12px 12px"
                              : "4px 12px 12px 12px",
                            bgcolor: isAdmin
                              ? alpha(theme.palette.primary.main, 0.1)
                              : "action.hover",
                            border: `1px solid ${
                              isAdmin
                                ? alpha(theme.palette.primary.main, 0.2)
                                : theme.palette.divider
                            }`,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: "pre-wrap",
                              lineHeight: 1.65,
                              fontSize: "0.8375rem",
                            }}
                          >
                            {reply.message}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{
                              fontSize: "0.65rem",
                              mt: 0.5,
                              display: "block",
                            }}
                          >
                            {new Date(reply.createdAt).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </Stack>
              </Box>
            )}

            {/* Reply input */}
            {selectedTicket.status !== "closed" && (
              <Stack direction="row" spacing={1} alignItems="flex-end">
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  size="small"
                  placeholder={t("typeReply")}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendReply();
                    }
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
                <IconButton
                  onClick={handleSendReply}
                  disabled={!replyText.trim() || sending}
                  sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    borderRadius: 2,
                    width: 38,
                    height: 38,
                    flexShrink: 0,
                    "&:hover": { bgcolor: "primary.dark" },
                    "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
                  }}
                >
                  <SendIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
            )}
          </Stack>
        )}
      </CommonDialog>
    </Box>
  );
};

export default AdminSupportTickets;
